import { useState } from 'react'
import Icon from '../Icon'
import { api, type Me } from '../../lib/classroom'
import { L, w } from '../../lib/words'
import { celebrate, sound, buzz } from '../../lib/fun'
import { ITEMS, SLOTS, owns, type Item, type Look, type Slot } from '../../../shared/shop'
import Pichi, { stageFor } from './Pichi'

/**
 * La Tienda de Pichi. Tap anything to try it on — the big Pichi shows it
 * straight away — then buy it with XP, or wear what you already own.
 * The server checks the price and the balance; this page only asks.
 */
export default function Shop({ me, refresh }: { me: Me; refresh: () => Promise<void> }) {
  const [slot, setSlot] = useState<Slot>('hat')
  const [trying, setTrying] = useState<Item | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview: Look = trying ? { ...me.look, [trying.slot]: trying.id } : me.look
  const stage = Math.max(1, stageFor(me.level.index))

  const act = async (fn: () => Promise<unknown>, party = false) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await refresh()
      if (party) { celebrate(); sound('level'); buzz([30, 40, 30]) } else sound('tap')
      setTrying(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const t = trying
  const tOwned = t ? owns(me.owned, t.id) : false
  const tWearing = t ? me.look[t.slot] === t.id : false
  const tNeedsBase = !!t?.base && !owns(me.owned, t.base)
  const tExclusive = !!t?.exclusive
  const baseName = (id?: string) => L(ITEMS.find((x) => x.id === id)?.title)

  return (
    <div className="shop">
      <section className={`card shop-stage bg-${preview.bg ?? 'mar'}`}>
        <Pichi size={180} stage={stage} mood={t ? 'wow' : 'happy'} look={preview} />
        <div className="shop-wallet"><Icon name="coin" /> {w('wallet')} <b>{me.wallet.toLocaleString()}</b> {w('xpSpend')}</div>
      </section>

      {t && (
        <section className="card try-card">
          <strong>{L(t.title)}</strong>
          {tWearing ? (
            <button disabled={busy || t.slot === 'color' || t.slot === 'bg'} onClick={() => act(() => api.equip(t.slot, null))}>{w('takeOff')}</button>
          ) : tOwned ? (
            <button className="primary narrow" disabled={busy} onClick={() => act(() => api.equip(t.slot, t.id))}>{w('wear')}</button>
          ) : tExclusive ? (
            me.champion
              ? <button className="primary narrow" disabled={busy} onClick={() => act(() => api.equip(t.slot, t.id), true)}>{w('wear')}</button>
              : <span className="small need-base"><Icon name="lock" size={16} /> {w('exclusive')}</span>
          ) : tNeedsBase ? (
            <span className="small need-base"><Icon name="lock" size={16} /> {w('needBase')} {baseName(t.base)}</span>
          ) : me.wallet >= t.price ? (
            <button className="primary narrow" disabled={busy} onClick={() => act(() => api.buy(t.id), true)}><Icon name="coin" size={18} /> {w('buyFor')} {t.price}</button>
          ) : (
            <span className="muted small">{w('needMore')} {(t.price - me.wallet).toLocaleString()} {w('oro')}</span>
          )}
        </section>
      )}
      {error && <p className="error">{error}</p>}

      <div className="tabs shop-tabs">
        {SLOTS.map((s) => (
          <button key={s.slot} aria-current={slot === s.slot} onClick={() => { setSlot(s.slot); setTrying(null) }}>{L(s.title)}</button>
        ))}
      </div>

      <div className="shop-grid">
        {ITEMS.filter((i) => i.slot === slot).map((i) => {
          const mine = owns(me.owned, i.id)
          const on = me.look[i.slot] === i.id
          return (
            <button key={i.id} className={`shop-item${trying?.id === i.id ? ' trying' : ''}${on ? ' on' : ''}`} onClick={() => { setTrying(i); sound('tap') }}>
              <span className={`shop-thumb bg-${i.slot === 'bg' ? i.id : 'plain'}`}>
                <Pichi size={76} stage={stage} look={{ ...me.look, [i.slot]: i.id }} />
              </span>
              <span className="shop-name">{L(i.title)}</span>
              <span className={`shop-price${on || mine ? '' : i.exclusive ? (me.champion ? ' can' : ' cant') : i.base && !owns(me.owned, i.base) ? ' cant' : me.wallet >= i.price ? ' can' : ' cant'}`}>
                {on ? w('wearing') : i.exclusive ? <><Icon name={me.champion ? 'trophy' : 'lock'} size={14} /> {me.champion ? w('owned') : w('gold')}</> : mine ? w('owned') : <>{i.base && !owns(me.owned, i.base) ? <Icon name="lock" size={14} /> : <Icon name="coin" size={14} />} {i.price}</>}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
